/**
 * Firebase Authentication Error Handling Utility
 * 
 * This module provides user-friendly error messages for Firebase authentication errors.
 * It translates technical Firebase error codes into clear, actionable messages.
 */

export interface AuthErrorMessage {
  title: string;
  description: string;
}

/**
 * Maps Firebase authentication error codes to user-friendly error messages
 */
const AUTH_ERROR_MESSAGES: Record<string, AuthErrorMessage> = {
  // Login errors
  'auth/invalid-credential': {
    title: 'Invalid Credentials',
    description: 'The email or password you entered is incorrect. Please check your credentials and try again.',
  },
  'auth/user-not-found': {
    title: 'Account Not Found',
    description: "We couldn't find an account with this email address. Please check the email or sign up for a new account.",
  },
  'auth/wrong-password': {
    title: 'Incorrect Password',
    description: 'The password you entered is incorrect. Please try again or reset your password if you forgot it.',
  },
  'auth/invalid-email': {
    title: 'Invalid Email',
    description: 'Please enter a valid email address.',
  },
  'auth/user-disabled': {
    title: 'Account Disabled',
    description: 'Your account has been disabled. Please contact support for assistance.',
  },
  'auth/too-many-requests': {
    title: 'Too Many Attempts',
    description: 'We detected too many failed login attempts. Please try again later or reset your password.',
  },

  // Signup errors
  'auth/email-already-in-use': {
    title: 'Email Already Registered',
    description: 'An account with this email address already exists. Please log in or use a different email.',
  },
  'auth/weak-password': {
    title: 'Weak Password',
    description: 'Your password is too weak. Please choose a stronger password with at least 6 characters.',
  },
  'auth/operation-not-allowed': {
    title: 'Registration Unavailable',
    description: 'Account registration is currently unavailable. Please contact support.',
  },

  // Password reset errors
  'auth/invalid-action-code': {
    title: 'Invalid or Expired Link',
    description: 'This password reset link has expired or is invalid. Please request a new one.',
  },
  'auth/expired-action-code': {
    title: 'Link Expired',
    description: 'This password reset link has expired. Please request a new password reset email.',
  },

  // Re-authentication errors
  'auth/requires-recent-login': {
    title: 'Re-authentication Required',
    description: 'For security reasons, please log out and log back in before making this change.',
  },

  // Email update errors
  'auth/email-already-exists': {
    title: 'Email Already In Use',
    description: 'This email address is already associated with another account. Please use a different email.',
  },

  // Network errors
  'auth/network-request-failed': {
    title: 'Network Error',
    description: 'Unable to connect to the server. Please check your internet connection and try again.',
  },
  'auth/timeout': {
    title: 'Request Timeout',
    description: 'The request took too long to complete. Please check your internet connection and try again.',
  },

  // Generic errors
  'auth/internal-error': {
    title: 'Something Went Wrong',
    description: 'An unexpected error occurred. Please try again later or contact support if the problem persists.',
  },
};

/**
 * Default error message for unknown error codes
 */
const DEFAULT_ERROR: AuthErrorMessage = {
  title: 'Authentication Error',
  description: 'An unexpected error occurred. Please try again or contact support if the problem persists.',
};

/**
 * Extracts the Firebase error code from an error object
 */
function getErrorCode(error: any): string | null {
  if (typeof error === 'string') {
    return null;
  }
  return error?.code || null;
}

/**
 * Gets a user-friendly error message for a Firebase authentication error
 * 
 * @param error - The error object from Firebase or a custom error
 * @param context - Optional context for more specific error messages (e.g., 'login', 'signup', 'reset')
 * @returns An object with title and description for displaying to the user
 */
export function getAuthErrorMessage(
  error: any,
  context?: 'login' | 'signup' | 'reset' | 'update'
): AuthErrorMessage {
  const errorCode = getErrorCode(error);

  // If we have a Firebase error code, use the mapped message
  if (errorCode && AUTH_ERROR_MESSAGES[errorCode]) {
    return AUTH_ERROR_MESSAGES[errorCode];
  }

  // If the error has a message property, try to make it more user-friendly
  if (error?.message) {
    const message = error.message.toLowerCase();

    // Check for common patterns in error messages
    if (message.includes('network') || message.includes('connection')) {
      return AUTH_ERROR_MESSAGES['auth/network-request-failed'];
    }

    if (message.includes('password')) {
      if (context === 'login') {
        return AUTH_ERROR_MESSAGES['auth/wrong-password'];
      } else if (context === 'signup') {
        return AUTH_ERROR_MESSAGES['auth/weak-password'];
      }
    }

    if (message.includes('email')) {
      if (message.includes('already') || message.includes('exists')) {
        return AUTH_ERROR_MESSAGES['auth/email-already-in-use'];
      }
      return AUTH_ERROR_MESSAGES['auth/invalid-email'];
    }

    if (message.includes('user not found')) {
      return AUTH_ERROR_MESSAGES['auth/user-not-found'];
    }

    // If we have a readable error message, use it as the description
    return {
      title: context === 'login' ? 'Login Failed' : 
             context === 'signup' ? 'Signup Failed' :
             context === 'reset' ? 'Password Reset Failed' :
             context === 'update' ? 'Update Failed' :
             'Authentication Error',
      description: error.message,
    };
  }

  // Return default error message
  return DEFAULT_ERROR;
}

/**
 * Gets a simple error description string (for backward compatibility)
 */
export function getAuthErrorDescription(error: any, context?: 'login' | 'signup' | 'reset' | 'update'): string {
  const errorMessage = getAuthErrorMessage(error, context);
  return errorMessage.description;
}

